import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { BehaviorSubject } from "rxjs";
import { debounceTime, distinctUntilChanged, filter } from "rxjs/operators";
import { SubSink } from "subsink";
import { UserToken } from "../../../../auth/user-token.model";
import { MessagingService } from "../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../core/messaging/severity-type.enum";
import { ParameterService } from "../../../../core/navigation/parameter.service";
import { SessionService } from "../../../../core/storage/session.service";
import { UserService } from "../../../../core/user/user.service";
import { FormService } from "../../../../dynamic-forms/form.service";
import { Dropdown } from "../../../../dynamic-forms/inputs/dropdown/dropdown.model";
import { SelectableInput } from "../../../../dynamic-forms/inputs/selectable-input.model";
import { Switch } from "../../../../dynamic-forms/inputs/switch/switch.model";
import { MedicalRecordUploadService } from "../../../../platform/api/medical-record-upload/medical-record-upload.service";
import { WorkflowStatusDb } from "../../../../platform/api/workflow/workflow-status-db.enum";
import { JobsqueueService } from "../../../../platform/modules/jobsqueue/jobsqueue.service";
import { ChartService } from "../../../../platform/modules/member/chase-detail/chase-detail-chart/chart.service";
import { ChaseDetailStateService } from "../../../../platform/modules/member/chase-detail/chase-detail-state.service";
import { ChaseDocumentService } from "../../../../platform/modules/member/chase-document.service";
import { MoveBackChasePageItem } from "../../../../platform/modules/member/move-back-chase-page-item-model";
import { MoveBackChasePageRequest } from "../../../../platform/modules/member/move-back-chase-page-request.model";
import { MoveBackChasePageType } from "../../../../platform/modules/member/move-back-chase-page-type.enum";
import { BulkUpdate } from "../../../../platform/modules/project/bulk-updates/bulk-update.model";
import { DirectoryUserRole } from "../../../../platform/modules/retrieval/directory-user-role";
import { DocumentPage } from "../../../../platform/modules/retrieval/retreival-document-review/document-page.model";
import { DocumentPages } from "../../../../platform/modules/retrieval/retreival-document-review/document-pages.models";
import { RetrievalDocumentServiceService } from "../../../../platform/modules/retrieval/retrieval-document-service.service";
import { DocumentSourceType } from "../../../../platform/modules/retrieval/retrieval-document-source-type.enum";
import { ArrayHelper } from "../../../../utilities/contracts/array-helper";
import { NumberHelper } from "../../../../utilities/contracts/number-helper";
import { StringHelper } from "../../../../utilities/contracts/string-helper";
import { debounceTimeAfterFirst } from "../../../../utilities/debounce-time-after";
import { LoaderHelper } from "../../../../utilities/loader-helper.class";
import { BulkAction } from "../../../grid/bulk-actions/bulk-action.model";
import { MemberCentricChase } from "../../../membercentric-doc-attachment/membercentric-chase.model";
import { IOnUpload, UploadComponent } from "../../../upload/upload.component";
import { DocumentListItem } from "../../chase-document-list/document-list-item.model";
import { DocumentPageService } from "../../document-page.service";
import { DocumentRequestService } from "../../document-request.service";
import { DocumentPageViewerComponent } from "../document-page-viewer.component";
import { DocumentThumbnailState } from "../document-thumbnail-state.model";
import { DocumentThumbnailType } from "../document-thumbnail-type.enum";
import { DocumentViewerSessionService } from "../document-viewer-session.service";

@Component({
  selector: "app-document-page-thumbnail-expand",
  templateUrl: "./document-page-thumbnail-expand.component.html",
  styleUrls: ["./document-page-thumbnail-expand.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentPageThumbnailExpandComponent extends LoaderHelper implements OnInit, OnChanges, OnDestroy {
  static documentId: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  @ViewChild(CdkVirtualScrollViewport, { static: true }) private thumbnailExpandedViewport: CdkVirtualScrollViewport;
  @ViewChild("fileUpload", { static: true }) fileUpload: UploadComponent;
  @Input() chaseId: number;
  @Input() addressId: number;
  @Input() projectId: number;
  @Input() currentChartPageNumber = 1;
  @Input() isMaximized = false;

  @Output() toggleThumbnailViewClicked = new EventEmitter<{ view: string; pageNumber: number }>();

  private readonly THUMBNAILKEY = "THUMBNAIL_FILTER";
  private readonly NUMBER_OF_THUMBNAILS_PER_ROW = 5;
  private currentThumbnailNumber$ = new BehaviorSubject<number>(0);
  private sink = new SubSink();
  private state: DocumentThumbnailState;
  readonly itemSize = 274;

  formGroup: FormGroup;
  thumbnailFilter: Dropdown;
  clearCache = false;
  selectedThumbnail: number;
  thumbnailFilterValue: number;
  docType: string;

  moveBackDocumentEnabled: Switch;
  moveBackChasePageTypeOptions: BulkAction[] = [];
  moveBackChasePageType: Dropdown;
  selectedItems: MoveBackChasePageItem[] = [];
  selectedChasePageMoveType: string;
  private user: UserToken;
  hasMoveBackPermission: boolean;
  isRequestDocumentModalOpen = false;
  uploadSuccessMessage = "Thank you for uploading this medical record.  We are now processing the file.  Please check back shortly and your document will be attached.";
  uploadSuccessMessage1 = "Thank you for uploading medical record : ";
  uploadSuccessMessage2 = ".  We are now processing the file.  Please check back shortly and your document will be attached.";
  isConfirmMoveBackAllVisible = false;
  isChaseBeyondMrr = false;
  chartDataEntryPages: number[];
  memberChases: MemberCentricChase[];
  totalChartPages: number;
  currentDocQueueId: number = null;
  isFromToggledView = false;
  cdnBucketFolders = [];
  currentSpineColorIndex = 0;
  prevMouseClick = 0;
  isBucketFolderChecked = false;
  documentListItems: DocumentListItem[];
  maxDocuments = 0;
  isPageJump = true;
  usePresignedURLs: boolean;
  @Output() updateTotalPages = new EventEmitter<number>();
  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private documentRequestService: DocumentRequestService,
    private readonly documentViewerSessionService: DocumentViewerSessionService,
    private readonly chaseDetailStateService: ChaseDetailStateService,
    private readonly chartService: ChartService,
    private formService: FormService,
    private parameterService: ParameterService,
    private sessionService: SessionService,
    private jobsqueueService: JobsqueueService,
    private medicalRecordUploadService: MedicalRecordUploadService,
    private messagingService: MessagingService,
    private userService: UserService,
    private chaseDocumentService: ChaseDocumentService,
    protected readonly zone: NgZone,
    protected readonly retrievalDocumentService: RetrievalDocumentServiceService,
    private documentPageService: DocumentPageService
  ) {
    super(zone);
  }

  get cleanDocumentId(): number {
    return Number(DocumentPageViewerComponent.documentId.getValue());
  }

  get pages$(): BehaviorSubject<(DocumentPage | null)[]> {
    return this.state.pages$;
  }

  get pages(): DocumentPage[] {
    return this.state.pages;
  }

  get allThumbnails(): any[] {
    const pages = [...this.currentPagesWithCoversheet];
    return ArrayHelper.chunkArray(pages, this.NUMBER_OF_THUMBNAILS_PER_ROW);
  }

  getEvidencedata(item: any, val: any): string {
    const evidenceData: any = [];
    if (val === "text") {
      item.map(ele => { evidenceData.push(ele.text); }).join(",");
      return evidenceData;
    }
    if (val === "chartlocation") {
      item.map(ele => { evidenceData.push(ele.chartLocation); }).join(",");
      return evidenceData;
    }
    return evidenceData;
  }

  get currentThumbnailNumber(): number {
    return this.state.currentThumbnailNumber;
  }
  set currentThumbnailNumber(value: number) {
    this.state.currentThumbnailNumber = value;
    this.changeDetector.markForCheck();
  }

  get thumbnailEditMode(): boolean {
    return this.state.thumbnailEditMode;
  }
  set thumbnailEditMode(value: boolean) {
    this.state.thumbnailEditMode = value;
    this.changeDetector.markForCheck();
  }

  get currentDocumentQueueId(): number {
    return this.state.currentDocumentQueueId;
  }
  set currentDocumentQueueId(value: number) {
    this.state.currentDocumentQueueId = value;
    this.changeDetector.markForCheck();
  }

  get hasCurrentPage(): boolean {
    return this.state.hasCurrentPage;
  }

  get totalPages(): number {
    return this.state.totalPages;
  }

  get isEditMode(): boolean {
    return this.isEditMode;
  }

  get moveChaseBackConfirmationText(): string {
    return this.removeAllPages ?
      "You are about to remove all pages for this chase. This will result in all chases for this member to move back to chart collection. Do you want to continue?"
      : "You are about to remove pages that were used during data entry. This will move all chases back to data entry. Do you want to continue?";
  }

  get removeAllPages(): boolean {
    return this.selectedItems.length === this.totalChartPages;
  }

  get isMoveBackMemberChasesToAbstraction(): boolean {
    return (this.isChaseBeyondMrr && this.selectedItems.some(s => this.chartDataEntryPages.includes(s.pageNumber)));
  }
  ngOnInit() {
    this.state = new DocumentThumbnailState();
    this.sink.add(
      this.currentThumbnailNumber$
        .pipe(debounceTimeAfterFirst(50))
        .subscribe(pageNumber => {
          this.currentThumbnailNumber = pageNumber;
        }),
      this.chaseDetailStateService.isChaseBeyondMrr.subscribe(data => {
        this.isChaseBeyondMrr = data;
      }),
      this.chaseDetailStateService.memberChases.subscribe(data => {
        this.memberChases = data;
      }),
      this.jobsqueueService.newJobEvent.subscribe(data => {
        if (data.find(d => d.stateMachineName === "MRCS Chart Upload")) {
          this.resetThumbnailView();
          this.initialThumbnailLoad();
          this.changeDetector.markForCheck();
        }
      })
    );

    this.setMoveBackProcess();
    this.initializeForm();
    this.getThumbnailFilterOptions();
    this.setThumbnailFilter();
    this.initialThumbnailLoad();
    this.getDocumentListItems();
    this.changeDetector.detectChanges();
    this.sink.add(
      this.scrollIndexChange$.pipe(
        filter(v => NumberHelper.isAvailable(v)),
        debounceTime(500)
      ).subscribe(index => this.updatePageAndIndex(index)),
      this.documentPageService.chartPageChange$?.pipe(
        distinctUntilChanged(),
        debounceTime(100),
        filter(n => NumberHelper.isAvailable(n) && n !== this.currentChartPageNumber)
      ).subscribe(index => {
        const pageNumber = (index - 1);
        this.currentChartPageNumber = pageNumber;
        this.currentThumbnailNumber = pageNumber;
        this.state.currentThumbnailNumber = pageNumber;
        this.selectedThumbnail = index;
        this.thumbnailExpandedViewport.scrollToIndex(Math.floor(this.selectedThumbnail / this.NUMBER_OF_THUMBNAILS_PER_ROW));
      })
    );
    this.usePresignedURLs = this.userService.getUserToken().isPreSignedUrlUploadEnabled;
  }

  @HostListener("window:beforeunload")
  ngOnDestroy() {
    this.sink.unsubscribe();
    this.cleanCacheAfterNGDestory();
    this.resetThumbnailView();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.currentChartPageNumber) {
      if (changes.currentChartPageNumber.currentValue !== changes.currentChartPageNumber.previousValue) {
        this.currentThumbnailNumber$.next(changes.currentChartPageNumber.currentValue);
        this.selectedThumbnail = changes.currentChartPageNumber.currentValue;
        this.thumbnailExpandedViewport.scrollToIndex(Math.floor(this.selectedThumbnail / this.NUMBER_OF_THUMBNAILS_PER_ROW));

        this.changeDetector.markForCheck();
      }
    }
  }

  initializeForm(): void {
    this.thumbnailFilter = new Dropdown({
      key: "thumbnailFilter",
    });

    this.moveBackDocumentEnabled = new Switch({
      key: "moveBackDocumentEnabled",
      followingOnLabel: "MANAGE",
    });

    this.formGroup = this.formService.createFormGroup([this.thumbnailFilter, this.moveBackDocumentEnabled]);
  }

  setMoveBackProcess(): void {
    this.user = this.userService.getUserToken();
    const directoryRoleIdsForMoveBackPermission = [
      DirectoryUserRole.Admin,
      DirectoryUserRole.ClientLead,
      DirectoryUserRole.ClinicalLead,
      DirectoryUserRole.MRRManager,
      DirectoryUserRole.OverreadManager,
      DirectoryUserRole.ClientOverreadManager,
      DirectoryUserRole.ManageDocPages];
    const userContainsMoveBackRole = this.user.directoryRoleIds.filter(d => directoryRoleIdsForMoveBackPermission.includes(d));
    this.hasMoveBackPermission = ArrayHelper.isAvailable(userContainsMoveBackRole);
    this.chasePagesMoveBackReasons();

    this.getDataEntryPages();
  }

  getDataEntryPages(): void {
    this.chartService.getDataEntryPages(this.chaseId).subscribe(data => {
      this.chartDataEntryPages = data;
    });
  }

  chasePagesMoveBackReasons(): void {
    for (const element of Object.keys(MoveBackChasePageType)) {
      if (!isNaN(Number(element))) {
        const x = String(MoveBackChasePageType[element]);
        const reasonType = x.replace(/([a-z])([A-Z])/g, "$1 $2");

        this.moveBackChasePageTypeOptions.push(new BulkAction
          ({
            name: reasonType,
            action: this.inititateMoveBackChasePages.bind(this),
          }));
      }
    }
  }

  moveBackChasePages(): void {
    const moveBackRequest = new MoveBackChasePageRequest({
      moveBackChasePageType: Number(this.selectedChasePageMoveType),
      moveBackChasePageItems: this.selectedItems,
    });

    this.retrievalDocumentService.moveBackChasePages(moveBackRequest).subscribe(
      () => {
        if (MoveBackChasePageType.Remove === moveBackRequest.moveBackChasePageType && ArrayHelper.isAvailable(this.selectedItems)) {
          this.updateTotalPages.next(this.selectedItems.length);
        }
        this.messagingService.showToast("Moved back Chase Pages.", SeverityType.SUCCESS);
        if (this.isMoveBackMemberChasesToAbstraction && !this.removeAllPages) {
          this.moveBackMemberChasesToAbstraction();
        }
        this.sessionService.delete(this.THUMBNAILKEY);
        this.resetThumbnailView();
        this.initialThumbnailLoad();
      },
      err => {
        this.messagingService.showToast("Failed to Moved back Chase Pages.", SeverityType.ERROR);
      });
  }

  inititateMoveBackChasePages(): void {
    if (this.removeAllPages || this.isMoveBackMemberChasesToAbstraction) {
      this.isConfirmMoveBackAllVisible = true;
    } else {
      this.moveBackChasePages();
    }
  }

  getDocumentListItems(): void {
    this.chaseDocumentService
      .getDocumentListItems(this.chaseId, this.maxDocuments)
      .subscribe(result => {
        this.documentListItems = result;
        this.currentPagesWithCoversheet = [...this.updateThumbnailStyle(this.currentPages, result)];
        this.changeDetector.markForCheck();
      });
  }

  private moveBackMemberChasesToAbstraction(): void {
    const chaseToMoveBacktoAbstraction = this.memberChases.filter(m => m.workflowStatusId === WorkflowStatusDb.Overread
      || m.workflowStatusId === WorkflowStatusDb.Overread2
      || m.workflowStatusId === WorkflowStatusDb.Delivery
      || m.workflowStatusId === WorkflowStatusDb.Completed);

    const moveBackMemberChasesModel = new BulkUpdate({
      chaseIds: chaseToMoveBacktoAbstraction.map(c => c.chaseId.toString()),
      status: WorkflowStatusDb.Abstraction.toString(),
      action: 1,
      chaseRequestUpdate: 1,
    });
    this.retrievalDocumentService.moveChases(moveBackMemberChasesModel).subscribe(
      () => {
        this.messagingService.showToast("Moved back All Member Chase(s) to Data Entry.", SeverityType.SUCCESS);
        this.changeDetector.markForCheck();
      },
      err => {
        this.messagingService.showToast("Failed to Move back All Member Chase Pages to Data Entry.", SeverityType.ERROR);
        this.changeDetector.markForCheck();
      });
  }

  updateCurrentPage(index: number): void {
    const pageNumber = index * this.NUMBER_OF_THUMBNAILS_PER_ROW;
    this.scrollToIndex(pageNumber);
    this.documentViewerSessionService.updateDataEntryPageNumber(index);
  }

  hasPageAndSource(page: DocumentPage): boolean {
    return page != null && StringHelper.isAvailable(page.source);
  }

  hasThumbnailImageUrl(thumbnail: DocumentPage): boolean {
    return StringHelper.isAvailable(thumbnail.image);
  }

  hasThumbnails(): boolean {
    return ArrayHelper.isAvailable(this.pages);
  }

  isThumbnailSelected(thumbnail: DocumentPage): boolean {
    if (this.thumbnailEditMode) {
      return thumbnail.isThumbnailSelectedInEditMode;
    } else {
      return this.selectedThumbnail === thumbnail.pageNumber;
    }
  }
  updateIndex(index): void {
   this.scrollIndexChange$.next(index);
  }
  private updatePageAndIndex(index: number): void {
    if (index !== this.currentThumbnailNumber) {
      const pageNumber = index * this.NUMBER_OF_THUMBNAILS_PER_ROW;
      this.currentThumbnailNumber = pageNumber;
      this.state.currentThumbnailNumber = pageNumber;
      this.currentThumbnailNumber = pageNumber;
      this.loadThumbnails(pageNumber);
    }
  }
  loadThumbnails(pageNumber: number): void {
    // Need to add + 1 since the array starts at 0 but the pages start at 1
    const actualPageNumber = pageNumber + 1;
    const isInRange = this.isInRange(actualPageNumber, this.startRange, this.endRange, this.currentPages, "image");
    if (!isInRange || this.currentPages.length <= 0) {
      return;
    }
    const pagesToLoad = this.getPagesToLoad(pageNumber, this.lowerLoadRange, this.upperLoadRange, this.currentPages, "image");
    if (!ArrayHelper.isAvailable(pagesToLoad)) {
      return;
    }
    this.loadThumbnailsPages(
      this.retrievalDocumentService,
      "getDocumentThumbnailsWithDocQueueId",
      pagesToLoad,
      this.cleanDocumentId,
      this.cdnBucketFolders,
      this.isBucketFolderChecked,
      this.thumbnailFilterValue,
      (documentPages => {
        this.cdnBucketFolders = [...documentPages.s3ImagesFolders];
        this.setPages([...this.currentPages]);
      }));
  }
  getThumbnailSource(thumbnail: DocumentPage): string {
    return this.hasThumbnailImageUrl ? (thumbnail.isCdnChartImageExist ? thumbnail.source.replace("data:image/jpg;base64,", "")
      : thumbnail.source) : "";
  }

  isDocumentThumbnail(thumbnail: DocumentPage): boolean {
    return thumbnail.documentThumbnail.thumbnailType === DocumentThumbnailType.THUMBNAIL_IMAGE;
  }

  isCoversheetThumbnail(thumbnail: DocumentPage): boolean {
    return thumbnail.documentThumbnail.thumbnailType === DocumentThumbnailType.COVERSHEET;
  }

  toggleThumbnailView(view: string): void {
    this.toggleThumbnailViewClicked.emit({ view, pageNumber: this.selectedThumbnail });
  }

  thumbnailFilterChange(event: SelectableInput): void {
    this.thumbnailFilterValue = Number(event.value);
    this.sessionService.put(this.THUMBNAILKEY, this.thumbnailFilterValue);
    this.resetMoveBackSwitch();
    this.thumbnailEditMode = false;
    this.isBucketFolderChecked = false;
    this.cleanCacheAfterNGDestory();
    this.initialThumbnailLoad();
    this.changeDetector.markForCheck();
  }

  thumbnailClicked(thumbnail: DocumentPage): void {
    if (this.thumbnailEditMode) {
      thumbnail.isThumbnailSelectedInEditMode = !thumbnail.isThumbnailSelectedInEditMode;
      this.getDataForMoveBack(thumbnail);
    } else {
      this.updateCurrentPage(thumbnail.pageNumber);
    }

    const thisClick = Date.now();

    if (thisClick - this.prevMouseClick < 250) {
      this.toggleThumbnailView("collapse");
    }

    this.prevMouseClick = thisClick;
  }

  coversheetClicked(thumbnail: DocumentPage): void {
    if (this.thumbnailEditMode) {
      const filteredDocumentQueueIdPages: DocumentPage[] = this.pages.filter(page => page?.documentQueueId === thumbnail.documentQueueId);
      const isAllPagesSelectedForEdit = filteredDocumentQueueIdPages.every(filteredpage => filteredpage.isThumbnailSelectedInEditMode);

      filteredDocumentQueueIdPages.forEach(filteredthumbnail => {
        if (isAllPagesSelectedForEdit) {
          filteredthumbnail.isThumbnailSelectedInEditMode = false;
          this.getDataForMoveBack(filteredthumbnail);
        } else {
          if (!filteredthumbnail.isThumbnailSelectedInEditMode) {
            filteredthumbnail.isThumbnailSelectedInEditMode = true;
            this.getDataForMoveBack(filteredthumbnail);
          }
        }
      });
    }
  }

  nonThumbnailClicked(thumbnail: DocumentPage): void {
    thumbnail.documentThumbnail.thumbnailType === DocumentThumbnailType.ADD_PAGE ? this.triggerUpload() : this.openAdditionalDocumentationModal();
  }

  triggerUpload() {
    const elementList = document.querySelectorAll('input[type="file"]');
    const clickEvent = new MouseEvent("click", {
      view: window,
      bubbles: true,
      cancelable: false,
    });
    elementList[0].dispatchEvent(clickEvent);
  }

  onChangeMoveBackDocumentEnabled(event: any): void {
    if (event.checked) {
      this.moveBackDocumentEnabled = new Switch({ ...this.moveBackDocumentEnabled, followingOnLabel: "" });
      this.selectedItems = [];
    } else {
      this.moveBackDocumentEnabled = new Switch({ ...this.moveBackDocumentEnabled, followingOnLabel: "MANAGE" });
      this.resetSelectedInEditModeThumbnails();
    }

    this.thumbnailEditMode = event.checked;
  }

  onSelectionChange(event: any): void {
    const selectedReasonString = String(event);
    const reasonEnum = selectedReasonString.replace(/\s/g, "");
    this.selectedChasePageMoveType = MoveBackChasePageType[reasonEnum];
  }

  getNonThumbnailIcon(thumbnail: DocumentPage): string {
    const typeOfThumbnail = thumbnail.documentThumbnail.thumbnailType;

    switch (typeOfThumbnail) {
      case DocumentThumbnailType.ADD_PAGE:
        return "plus";
      case DocumentThumbnailType.REQUEST_PAGE:
        return "tasks";
      default:
        return "";
    }
  }

  trackByIndex(index, item) {
    return index;
  }

  private getDataForMoveBack(thumbnail: DocumentPage) {
    this.state.setThumbnailPage(thumbnail);

    if (thumbnail.isThumbnailSelectedInEditMode) {
      const selectedItemToMoveBack = new MoveBackChasePageItem({
        documentQueueId: thumbnail.documentQueueId,
        chaseId: this.chaseId,
        pageNumber: thumbnail.pageNumber,
        documentPageNumber: thumbnail.documentPageNumber,
      });
      this.selectedItems = [...this.selectedItems, selectedItemToMoveBack];
    } else {
      const thumbnailToRemoveIndex = this.selectedItems.findIndex(x => x.pageNumber === thumbnail.pageNumber);
      const selectedItemsToMoveBack = [...this.selectedItems];

      if (NumberHelper.isGreaterThan(thumbnailToRemoveIndex, 0, true)) {
        selectedItemsToMoveBack.splice(thumbnailToRemoveIndex, 1);
        this.selectedItems = [...selectedItemsToMoveBack];
      }
    }
  }

  private resetSelectedInEditModeThumbnails() {
    this.pages.forEach(page => {
      if (page?.isThumbnailSelectedInEditMode) {
        page.isThumbnailSelectedInEditMode = false;
        this.state.setThumbnailPage(page);
      }
    });
  }

  private initialThumbnailLoad(): void {
    const pagesToLoad = [1, 15];
    pagesToLoad.forEach(pageNumber => this.state?.setPage({ pageNumber } as any));
    this.loadThumbnailsPages(
      this.retrievalDocumentService,
      "getDocumentThumbnailsWithDocQueueId",
      pagesToLoad,
      this.cleanDocumentId,
      this.cdnBucketFolders,
      this.isBucketFolderChecked,
      this.thumbnailFilterValue, thumbnailPages => {
        const length = (thumbnailPages && ArrayHelper.isAvailable(thumbnailPages.pages)) ? thumbnailPages.pages[0].numberOfPages : 0;
        // Set Pages
        this.currentDocumentQueueId = thumbnailPages.pages[0]?.documentQueueId;
        const thumbnailPagesLength = thumbnailPages.pages.length;
        const emptyArray: DocumentPage[] = new Array(length - thumbnailPagesLength).fill({});
        const pagestToSet = [...thumbnailPages.pages, ...emptyArray].map((page: DocumentPage, index) => {
          if (!StringHelper.isAvailable(page.image)) {
            return new DocumentPage({ pageNumber: index + 1 });
          }
          return page;
        });
        this.setPages(pagestToSet);
        if (NumberHelper.isGreaterThan(this.selectedThumbnail, 1)) {
          this.isFromToggledView = true;
        } else {
          this.currentThumbnailNumber = 1;
          this.scrollToIndex(0);
        }
        this.changeDetector.markForCheck();
      },
      () => { });
  }
  getCdnImages(documentPages: DocumentPages) {
    this.cdnBucketFolders = documentPages.s3ImagesFolders;
  }

  private scrollToIndex(pageNumber: number): void {
    this.thumbnailExpandedViewport.scrollToIndex(Math.floor(pageNumber / this.NUMBER_OF_THUMBNAILS_PER_ROW) - 1);
  }

  private setPages(pages: DocumentPage[]): void {
    this.currentPages = this.updatePageStyle(pages);
    this.currentPagesWithCoversheet = [...this.updateThumbnailStyle(pages, this.documentListItems)];
    this.changeDetector.markForCheck();
  }

  private cleanCacheAfterNGDestory(): void {
    this.clearCache = true;
    this.selectedItems = [];

    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        for (let i = 0; i < this.pages.length; ++i) {
          this.pages[i] = null;
        }
      });
    });
  }

  private getThumbnailFilterOptions(): void {
    this.documentRequestService
      .getDocumentThumbnailFilter(this.chaseId)
      .subscribe(options => {
        options.unshift(new SelectableInput({ text: "Complete Document File", value: null }));
        this.thumbnailFilter = new Dropdown({ ...this.thumbnailFilter, options } as any);
        this.changeDetector.markForCheck();
      });
  }

  private setThumbnailFilter(): void {
    if (this.hasDocTypeInUrl()) {
      this.thumbnailFilterValue = this.getDocTypeValue();
    } else if (this.hasSessionKey(this.THUMBNAILKEY)) {
      this.thumbnailFilterValue = this.getSessionFilterValue();
      this.sessionService.put(this.THUMBNAILKEY, this.thumbnailFilterValue);
    } else {
      this.thumbnailFilterValue = null;
    }
    this.formGroup.get(this.thumbnailFilter.key).setValue(this.thumbnailFilterValue);
  }

  private getSessionFilterValue(): number {
    const sessionValue = this.sessionService.get(this.THUMBNAILKEY, null);
    return StringHelper.isAvailable(sessionValue) ? Number(sessionValue) : sessionValue;
  }

  private hasSessionKey(sessionKey: string): boolean {
    const key = sessionKey;
    const filterKey = this.sessionService.get<any>(key, null);
    const hasKey = filterKey != null;
    return hasKey;
  }


  private hasDocTypeInUrl(): boolean {
    this.docType = this.parameterService.getNormal("docType", "");
    return StringHelper.isAvailable(this.docType);
  }

  private getDocTypeValue(): number {
    if (this.docType.toLowerCase() === "newpages") {
      return -999999999;
    }
  }

  private resetThumbnailView(): void {
    this.currentSpineColorIndex = 0;
    this.resetMoveBackSwitch();
    this.thumbnailEditMode = false;
    this.getThumbnailFilterOptions();
    this.setThumbnailFilter();
    this.changeDetector.markForCheck();
  }

  private resetMoveBackSwitch(): void {
    this.formGroup.get(this.moveBackDocumentEnabled.key).setValue(false);
    this.moveBackDocumentEnabled = new Switch({ ...this.moveBackDocumentEnabled, followingOnLabel: "MANAGE" });
    this.selectedItems = [];
  }


  openAdditionalDocumentationModal(): void {
    this.isRequestDocumentModalOpen = true;
  }

  get uploadData(): KeyValuePairs<string> {
    if (!this.projectId) {
      return null;
    } else {

      const projectIdValue = this.projectId == null ? "" : this.projectId;

      return [
        { key: "ChaseId", value: this.chaseId.toString() },
        { key: "ProjectId", value: projectIdValue.toString() },
        { key: "RetrievalTypeId", value: DocumentSourceType.MRRUpload.toString() },
      ];
    }
  }

  uploadDocument(event: IOnUpload): void {
    const documents = event.formData.getAll("Document");
    const chaseId = event.formData.get("ChaseId");
    const projectId = event.formData.get("ProjectId");
    const retrievalTypeId = event.formData.get("RetrievalTypeId");
    let fileNumCounter = 0;

    for (const document of documents) {
      const doc = (document as File);
      const fileName = doc.name;
      const newFormData = new FormData();

      newFormData.append("Document", document);
      newFormData.append("ChaseId", chaseId);
      newFormData.append("ProjectId", projectId);
      newFormData.append("RetrievalTypeId", retrievalTypeId);

      if (this.usePresignedURLs) {
        const name = event.presignedURLList[fileNumCounter.toString()];
        const OFN = event.originalFilenameList[fileNumCounter.toString()];
        newFormData.append("Size", doc.size.toString());
        newFormData.append("0", name);
        newFormData.append("OFN0", OFN);
        newFormData.append("NumFiles", "1");
        newFormData.delete("Document");
        fileNumCounter++;
        this.medicalRecordUploadService.uploadPresigned(newFormData).subscribe(
              data => {
                this.sessionService.delete(this.THUMBNAILKEY);
                this.resetThumbnailView();
                this.initialThumbnailLoad();
                this.changeDetector.markForCheck();
                this.fileUpload.deleteFile(fileName);
                this.messagingService.showToast(`${this.uploadSuccessMessage1}${fileName}${this.uploadSuccessMessage2}`, SeverityType.SUCCESS);
                event.success();
              },
              err => {
              }
            );
        } else {
          this.medicalRecordUploadService.upload(newFormData).subscribe(
            data => {
              this.sessionService.delete(this.THUMBNAILKEY);
              this.resetThumbnailView();
              this.initialThumbnailLoad();
              this.changeDetector.markForCheck();
              this.messagingService.showToast(`${this.uploadSuccessMessage1}${fileName}${this.uploadSuccessMessage2}`, SeverityType.SUCCESS);
              this.fileUpload.deleteFile(fileName);
            },
            err => {
              this.messagingService.showToast(`Error while uploading Document: ${fileName}, please try again.`, SeverityType.ERROR);
            }
          );
        }
    }
  }
}
